/**
 * Extraction recipes (§4.8.3, FR-50 – FR-53).
 *
 * A recipe records *how* to extract a ranking list from a source, so the second
 * and every subsequent refresh needs no user interaction. It is the thing that
 * turns BYOR from "paste your list again every Tuesday" into a source that
 * maintains itself.
 *
 * The critical property, and the reason `recipes/community/` may live in the
 * repository while `data/` may not (R2, FR-52): **a recipe contains only
 * extraction instructions — never player data.** No names, no ranks, no cached
 * rows. Anyone can share, PR, and redistribute a recipe without redistributing
 * anyone's rankings.
 *
 * Pure module — no I/O. Callers do the fetching and the storage.
 */

import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import type { ColumnRole, CandidateBlock, ExtractionResult } from './extract.ts';

export const RECIPE_VERSION = 1;

export interface Recipe {
  version: number;
  /** Stable slug used as the source_id and the filename. */
  slug: string;
  /** Friendly name shown in the UI and used for attribution (R3). */
  source_name: string;
  /** Domain the recipe applies to, for matching a pasted URL to a recipe. */
  domain: string | null;
  /**
   * How to find the page. Either a fixed URL, or a discovery rule that resolves
   * the most recent matching article from an index page — required for sources
   * that repost under new slugs (§4.2).
   */
  url: string | null;
  discovery: DiscoveryRule | null;
  /** Extraction strategy that won on the confirmed import. */
  strategy: CandidateBlock['strategy'];
  strategy_index: number;
  columns: ColumnRole[];
  /** Scoring format the list is expressed in, for the UI label. */
  scoring_format: 'standard' | 'half-ppr' | 'ppr' | 'unknown';
  /** Recorded at confirm time; replay compares against these (FR-51). */
  baseline: { confidence: number; rows: number };
  notes: string | null;
  created_at: string;
}

export interface DiscoveryRule {
  /** Index page listing the source's articles. */
  index_url: string;
  /** Regex matched against link text or href to pick the right article. */
  title_pattern: string;
  /** Prefer the first match in document order (usually the most recent). */
  pick: 'first' | 'last';
}

/** Replay guard thresholds (FR-51). */
export const REPLAY_GUARD = {
  /** Confidence drop, in percentage points, that triggers re-confirmation. */
  maxConfidenceDrop: 0.15,
  /** Fractional row-count change that triggers re-confirmation. */
  maxRowChange: 0.4,
} as const;

export function buildRecipe(
  args: {
    slug: string;
    source_name: string;
    url: string | null;
    discovery?: DiscoveryRule | null;
    block: CandidateBlock;
    scoring_format?: Recipe['scoring_format'];
    notes?: string | null;
  },
): Recipe {
  return {
    version: RECIPE_VERSION,
    slug: args.slug,
    source_name: args.source_name,
    domain: args.url ? safeHostname(args.url) : null,
    url: args.url,
    discovery: args.discovery ?? null,
    strategy: args.block.strategy,
    strategy_index: args.block.index,
    columns: args.block.columns,
    scoring_format: args.scoring_format ?? 'unknown',
    baseline: {
      confidence: round3(args.block.confidence),
      rows: args.block.total,
    },
    notes: args.notes ?? null,
    created_at: new Date().toISOString(),
  };
}

export type ReplayVerdict = 'ok' | 'needs-reconfirm' | 'failed';

export interface ReplayCheck {
  verdict: ReplayVerdict;
  reasons: string[];
  confidence_delta: number;
  row_delta: number;
}

/**
 * Decide whether a replayed extraction can be trusted (FR-51).
 *
 * This is how a silent site redesign gets caught instead of quietly corrupting
 * the board mid-draft. On anything suspicious the caller keeps the old data —
 * a stale ranking list is a far smaller problem on draft night than a subtly
 * wrong one, because the user can see that a date is old and cannot see that
 * rows 80-200 are now advertisements.
 */
export function checkReplay(recipe: Recipe, result: ExtractionResult): ReplayCheck {
  const reasons: string[] = [];
  const block = result.best;

  if (!block || block.total === 0) {
    return {
      verdict: 'failed',
      reasons: ['Replay produced no rows — the page layout has probably changed.'],
      confidence_delta: -recipe.baseline.confidence,
      row_delta: -1,
    };
  }

  const confidence_delta = block.confidence - recipe.baseline.confidence;
  const row_delta =
    recipe.baseline.rows === 0 ? 0 : (block.total - recipe.baseline.rows) / recipe.baseline.rows;

  if (confidence_delta < -REPLAY_GUARD.maxConfidenceDrop) {
    reasons.push(
      `Match confidence fell ${(Math.abs(confidence_delta) * 100).toFixed(0)} points (${(recipe.baseline.confidence * 100).toFixed(0)}% → ${(block.confidence * 100).toFixed(0)}%).`,
    );
  }
  if (Math.abs(row_delta) > REPLAY_GUARD.maxRowChange) {
    reasons.push(
      `Row count changed ${(row_delta * 100).toFixed(0)}% (${recipe.baseline.rows} → ${block.total}).`,
    );
  }
  if (result.rejected) {
    reasons.push(
      `Confidence ${(block.confidence * 100).toFixed(0)}% is below the ${(result.threshold * 100).toFixed(0)}% acceptance threshold.`,
    );
  }

  return {
    verdict: reasons.length === 0 ? 'ok' : 'needs-reconfirm',
    reasons,
    confidence_delta: round3(confidence_delta),
    row_delta: round3(row_delta),
  };
}

// ---------------------------------------------------------------------------
// Serialisation (FR-52) — small YAML files, shareable and PR-able
// ---------------------------------------------------------------------------

export function recipeToYaml(recipe: Recipe): string {
  return stringifyYaml(recipe, { lineWidth: 100 });
}

/**
 * Parse a recipe from YAML, validating strictly.
 *
 * Recipes are shared between users and PR'd into the repository, so a recipe is
 * untrusted input like any other. Validation rejects unknown strategies and
 * column roles rather than coercing them, and — importantly — refuses any
 * recipe carrying player data, which would be an R2 violation smuggled in
 * through the community directory.
 */
export function recipeFromYaml(text: string): { recipe: Recipe | null; errors: string[] } {
  const errors: string[] = [];
  let raw: unknown;
  try {
    raw = parseYaml(text);
  } catch (err) {
    return { recipe: null, errors: [`Not valid YAML: ${(err as Error).message}`] };
  }

  if (typeof raw !== 'object' || raw === null) {
    return { recipe: null, errors: ['Recipe must be a YAML mapping.'] };
  }
  const obj = raw as Record<string, unknown>;

  // R2 guard: a recipe is instructions, never data.
  for (const forbidden of ['rows', 'players', 'rankings', 'data', 'list']) {
    if (forbidden in obj) {
      errors.push(
        `Recipe contains a "${forbidden}" key. Recipes carry extraction instructions only — never player data.`,
      );
    }
  }

  const slug = str(obj['slug']);
  const source_name = str(obj['source_name']);
  const strategy = str(obj['strategy']);
  const validStrategies = ['html-table', 'html-list', 'text-numbered', 'text-delimited', 'json-embedded'];
  const validRoles = ['rank', 'name', 'team', 'position', 'tier', 'bye', 'notes', 'ignore'];

  if (!slug || !/^[a-z0-9][a-z0-9-]{0,48}$/.test(slug)) {
    errors.push('`slug` must be a lowercase kebab-case identifier.');
  }
  if (!source_name) errors.push('`source_name` is required (it is the attribution label).');
  if (!strategy || !validStrategies.includes(strategy)) {
    errors.push(`\`strategy\` must be one of: ${validStrategies.join(', ')}.`);
  }

  const columnsRaw = obj['columns'];
  const columns: ColumnRole[] = [];
  if (Array.isArray(columnsRaw)) {
    for (const c of columnsRaw) {
      if (typeof c === 'string' && validRoles.includes(c)) columns.push(c as ColumnRole);
      else errors.push(`Unknown column role: ${JSON.stringify(c)}`);
    }
  } else {
    errors.push('`columns` must be a list of column roles.');
  }

  const url = str(obj['url']);
  if (url && !/^https?:\/\//i.test(url)) {
    errors.push('`url` must be an http(s) URL.');
  }

  let discovery: DiscoveryRule | null = null;
  const disc = obj['discovery'];
  if (disc && typeof disc === 'object') {
    const d = disc as Record<string, unknown>;
    const index_url = str(d['index_url']);
    const title_pattern = str(d['title_pattern']);
    if (!index_url || !/^https?:\/\//i.test(index_url)) {
      errors.push('`discovery.index_url` must be an http(s) URL.');
    } else if (!title_pattern) {
      errors.push('`discovery.title_pattern` is required when discovery is used.');
    } else {
      try {
        new RegExp(title_pattern, 'i');
      } catch {
        errors.push('`discovery.title_pattern` is not a valid regular expression.');
      }
      discovery = {
        index_url,
        title_pattern,
        pick: d['pick'] === 'last' ? 'last' : 'first',
      };
    }
  }

  if (errors.length > 0) return { recipe: null, errors };

  const baseline = (obj['baseline'] ?? {}) as Record<string, unknown>;
  return {
    recipe: {
      version: typeof obj['version'] === 'number' ? obj['version'] : RECIPE_VERSION,
      slug: slug!,
      source_name: source_name!,
      domain: str(obj['domain']) ?? (url ? safeHostname(url) : null),
      url: url ?? null,
      discovery,
      strategy: strategy as CandidateBlock['strategy'],
      strategy_index: typeof obj['strategy_index'] === 'number' ? obj['strategy_index'] : 0,
      columns,
      scoring_format: (['standard', 'half-ppr', 'ppr'] as const).includes(
        obj['scoring_format'] as never,
      )
        ? (obj['scoring_format'] as Recipe['scoring_format'])
        : 'unknown',
      baseline: {
        confidence: typeof baseline['confidence'] === 'number' ? baseline['confidence'] : 0,
        rows: typeof baseline['rows'] === 'number' ? baseline['rows'] : 0,
      },
      notes: str(obj['notes']),
      created_at: str(obj['created_at']) ?? new Date().toISOString(),
    },
    errors: [],
  };
}

/**
 * Resolve an article URL from a discovery rule (§4.2).
 *
 * Article URL discovery must never be hardcoded to a single slug, because
 * sources repost and update under new URLs. This scans an index page's links
 * for one whose text or href matches the pattern. It takes already-fetched HTML
 * so the module stays pure — the caller owns the network.
 */
export function resolveDiscoveryUrl(indexHtml: string, rule: DiscoveryRule): string | null {
  let pattern: RegExp;
  try {
    pattern = new RegExp(rule.title_pattern, 'i');
  } catch {
    return null;
  }

  const anchors = indexHtml.match(/<a\b[^>]*href\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)[^>]*>[\s\S]*?<\/a\s*>/gi) ?? [];
  const matches: string[] = [];

  for (const anchor of anchors) {
    const hrefMatch = /href\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(anchor);
    const href = hrefMatch?.[2] ?? hrefMatch?.[3] ?? hrefMatch?.[4];
    if (!href) continue;
    const text = anchor.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (!pattern.test(text) && !pattern.test(href)) continue;

    try {
      matches.push(new URL(href, rule.index_url).toString());
    } catch {
      // Unparseable href — skip rather than fail the whole resolution.
    }
  }

  if (matches.length === 0) return null;
  return rule.pick === 'last' ? matches[matches.length - 1]! : matches[0]!;
}

// ---------------------------------------------------------------------------

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function safeHostname(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

const round3 = (n: number): number => Math.round(n * 1000) / 1000;
